import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import UndoIcon from "@mui/icons-material/Undo";
import axiosInstance from "../axiosInstance";
import MerchandisingRulesSection from "./MerchandisingRulesSection";

const SORT_OPTIONS = [
  { key: "relevance", label: "Relevance", description: "Use the normal relevance-ranked search." },
  { key: "best-selling", label: "Best selling", description: "Products with more recorded sales appear first." },
  { key: "name-asc", label: "Name: A to Z", description: "Sort product titles alphabetically." },
  { key: "name-desc", label: "Name: Z to A", description: "Sort product titles in reverse alphabetical order." },
  { key: "price-asc", label: "Price: Low to high", description: "Show the lowest eligible variant price first." },
  { key: "price-desc", label: "Price: High to low", description: "Show the highest eligible variant price first." },
  { key: "date-desc", label: "Newest first", description: "Show the most recently published products first." },
  { key: "date-asc", label: "Oldest first", description: "Show the earliest published products first." },
];

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor: "rgba(255,255,255,0.94)",
    "&.Mui-focused fieldset": { borderColor: "#f97316" },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: "#f97316" },
};

const fallbackPrimaryButtonSx = {
  borderRadius: "12px",
  textTransform: "none",
  fontWeight: 700,
  background: "linear-gradient(135deg, #fb923c, #f97316)",
  boxShadow: "none",
  "&:hover": { background: "linear-gradient(135deg, #f97316, #ea580c)", boxShadow: "none" },
};

const fallbackNeutralButtonSx = {
  borderRadius: "12px",
  textTransform: "none",
  fontWeight: 700,
  color: "#475569",
  borderColor: "rgba(15, 23, 42, 0.14)",
};

const getErrorMessage = (error, fallback) => {
  const data = error?.response?.data;
  if (typeof data === "string" && data.trim()) return data;
  if (typeof data?.message === "string" && data.message.trim()) return data.message;
  if (typeof data?.title === "string" && data.title.trim()) return data.title;
  return fallback;
};

const parseEnabledSorts = (value) => {
  if (value === undefined || value === null || String(value).trim() === "") {
    return SORT_OPTIONS.map(({ key }) => key);
  }

  let configured;
  if (Array.isArray(value)) configured = value;
  else {
    const raw = String(value).trim();
    try {
      configured = raw.startsWith("[") ? JSON.parse(raw) : raw.split(",");
    } catch {
      configured = [];
    }
  }

  const known = new Set(SORT_OPTIONS.map(({ key }) => key));
  return [...new Set(configured.map((key) => String(key).trim().toLowerCase()).filter((key) => known.has(key)))];
};

const reorder = (items, index, direction) => {
  const nextIndex = index + direction;
  if (nextIndex < 0 || nextIndex >= items.length) return items;
  const next = [...items];
  [next[index], next[nextIndex]] = [next[nextIndex], next[index]];
  return next;
};

const StatusBar = ({ dirty, saving, noun, onDiscard, onSave, saveDisabled, primaryButtonSx, neutralButtonSx }) => (
  <Paper elevation={0} sx={{ p: 1.5, borderRadius: "16px", border: "1px solid rgba(15,23,42,0.08)", backgroundColor: "rgba(255,255,255,0.96)" }}>
    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} alignItems={{ xs: "stretch", sm: "center" }} justifyContent="space-between">
      <Typography variant="body2" sx={{ color: dirty ? "#ea580c" : "#64748b", fontWeight: 700 }}>
        {dirty ? `You have unsaved ${noun} changes.` : `All ${noun} changes are saved.`}
      </Typography>
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button startIcon={<UndoIcon />} variant="outlined" onClick={onDiscard} disabled={!dirty || saving} sx={neutralButtonSx}>Discard</Button>
        <Button startIcon={saving ? <CircularProgress size={15} color="inherit" /> : <SaveOutlinedIcon />} variant="contained" onClick={onSave} disabled={!dirty || saving || saveDisabled} sx={primaryButtonSx}>
          {saving ? "Saving…" : `Save ${noun}`}
        </Button>
      </Stack>
    </Stack>
  </Paper>
);

const SortOptionsEditor = ({ tenantDetails, setTenantDetails, onCountChange, primaryButtonSx, neutralButtonSx }) => {
  const configuredValue = tenantDetails?.tenantAiProfile?.widgetConfig?.settingsConfig?.enabledProductSorts;
  const initialSorts = useMemo(() => parseEnabledSorts(configuredValue), [configuredValue]);
  const [orderedKeys, setOrderedKeys] = useState(initialSorts);
  const [savedKeys, setSavedKeys] = useState(initialSorts);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    setOrderedKeys(initialSorts);
    setSavedKeys(initialSorts);
    setFeedback(null);
  }, [initialSorts]);

  useEffect(() => onCountChange?.(orderedKeys.length), [onCountChange, orderedKeys.length]);

  const disabledKeys = SORT_OPTIONS.map(({ key }) => key).filter((key) => !orderedKeys.includes(key));
  const displayKeys = [...orderedKeys, ...disabledKeys];
  const dirty = JSON.stringify(orderedKeys) !== JSON.stringify(savedKeys);

  const toggleSort = (key, enabled) => {
    setOrderedKeys((current) => enabled ? [...current, key] : current.filter((item) => item !== key));
    setFeedback(null);
  };

  const moveSort = (key, direction) => {
    setOrderedKeys((current) => {
      const index = current.indexOf(key);
      return index < 0 ? current : reorder(current, index, direction);
    });
    setFeedback(null);
  };

  const saveSorts = async () => {
    const tenantId = tenantDetails?.id;
    if (!tenantId) return;
    setSaving(true);
    setFeedback(null);
    const widgetConfig = tenantDetails?.tenantAiProfile?.widgetConfig || {};
    const settingsConfig = {
      ...(widgetConfig.settingsConfig || {}),
      enabledProductSorts: JSON.stringify(orderedKeys),
    };
    try {
      await axiosInstance.put(`/tenant/widget-config/${encodeURIComponent(tenantId)}`, {
        themeConfig: widgetConfig.themeConfig || {},
        settingsConfig,
        pdpThemeConfig: widgetConfig.pdpThemeConfig || {},
      });
      setSavedKeys([...orderedKeys]);
      setTenantDetails?.((previous) => ({
        ...previous,
        tenantAiProfile: {
          ...previous?.tenantAiProfile,
          widgetConfig: { ...previous?.tenantAiProfile?.widgetConfig, settingsConfig },
        },
      }));
      setFeedback({ severity: "success", message: "Sort options saved successfully." });
    } catch (error) {
      setFeedback({ severity: "error", message: getErrorMessage(error, "Failed to save sort options.") });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={2}>
      <Alert severity="info" sx={{ borderRadius: "14px" }}>Enabled options are shown to shoppers in this order. An omitted sort still falls back to relevance.</Alert>
      {feedback ? <Alert severity={feedback.severity} sx={{ borderRadius: "12px" }}>{feedback.message}</Alert> : null}
      <StatusBar dirty={dirty} saving={saving} noun="sort options" onDiscard={() => { setOrderedKeys([...savedKeys]); setFeedback(null); }} onSave={saveSorts} primaryButtonSx={primaryButtonSx} neutralButtonSx={neutralButtonSx} />
      <Stack spacing={1}>
        {displayKeys.map((key) => {
          const option = SORT_OPTIONS.find((item) => item.key === key);
          const enabled = orderedKeys.includes(key);
          const enabledIndex = orderedKeys.indexOf(key);
          return (
            <Paper key={key} data-testid={`sort-option-${key}`} elevation={0} sx={{ px: 1.75, py: 1.25, borderRadius: "15px", border: "1px solid rgba(15,23,42,0.08)", opacity: enabled ? 1 : 0.68, backgroundColor: enabled ? "rgba(255,255,255,0.94)" : "rgba(248,250,252,0.78)" }}>
              <Stack direction="row" spacing={1.25} alignItems="center">
                <Stack direction="row" spacing={0.1} sx={{ flexShrink: 0 }}>
                  <Tooltip title="Move up"><span><IconButton size="small" aria-label={`Move ${option.label} up`} disabled={!enabled || enabledIndex === 0} onClick={() => moveSort(key, -1)}><ArrowUpwardIcon sx={{ fontSize: 17 }} /></IconButton></span></Tooltip>
                  <Tooltip title="Move down"><span><IconButton size="small" aria-label={`Move ${option.label} down`} disabled={!enabled || enabledIndex === orderedKeys.length - 1} onClick={() => moveSort(key, 1)}><ArrowDownwardIcon sx={{ fontSize: 17 }} /></IconButton></span></Tooltip>
                </Stack>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 800, color: "#202531" }}>{option.label}</Typography>
                  <Typography variant="body2" sx={{ color: "#64748b" }}>{option.description}</Typography>
                </Box>
                <FormControlLabel
                  label={<Typography variant="caption" sx={{ color: enabled ? "#15803d" : "#64748b", fontWeight: 700 }}>{enabled ? "Enabled" : "Disabled"}</Typography>}
                  labelPlacement="start"
                  control={<Switch checked={enabled} onChange={(event) => toggleSort(key, event.target.checked)} inputProps={{ "aria-label": `${option.label} enabled` }} sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#f97316" }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#f97316" } }} />}
                  sx={{ m: 0, flexShrink: 0 }}
                />
              </Stack>
            </Paper>
          );
        })}
      </Stack>
    </Stack>
  );
};

const normalizeFilter = (filter = {}) => ({
  name: String(filter.name ?? filter.Name ?? ""),
  field: String(filter.field ?? filter.Field ?? ""),
  enabled: Boolean(filter.enabled ?? filter.Enabled ?? true),
});

const fieldLabel = (field) => field
  .replace(/^Variants\./, "Variant · ")
  .replace(/^AiMandatoryFields\./, "Product · ")
  .replace(/^GlobalAttributes\./, "Product attribute · ")
  .replace(/^Variant · GlobalAttributes\./, "Variant attribute · ")
  .replace(/([a-z])([A-Z])/g, "$1 $2");

const fieldType = (field) => {
  if (field === "Variants.Price") return "Range";
  if (field.includes("GlobalAttributes.")) return "Auto";
  return "Options";
};

const validateFilters = (filters) => {
  const errors = filters.map(() => ({}));
  const names = new Map();
  filters.forEach((filter, index) => {
    const name = filter.name.trim();
    if (!name) errors[index].name = "Display name is required.";
    else {
      const key = name.toLowerCase();
      if (names.has(key)) {
        errors[index].name = `Name duplicates filter ${names.get(key) + 1}.`;
        errors[names.get(key)].name = `Name duplicates filter ${index + 1}.`;
      } else names.set(key, index);
    }
    if (!filter.field) errors[index].field = "Product field is required.";
  });
  return errors;
};

const ProductFiltersEditor = ({ tenantId, onCountChange, primaryButtonSx, neutralButtonSx }) => {
  const [filters, setFilters] = useState([]);
  const [savedFilters, setSavedFilters] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [feedback, setFeedback] = useState(null);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setLoadError("");
    setFeedback(null);
    try {
      const headers = { tenantId };
      const [filtersResponse, catalogResponse] = await Promise.all([
        axiosInstance.get("/product-filters", { headers }),
        axiosInstance.get("/product-filters/catalog", { headers }),
      ]);
      const nextFilters = (Array.isArray(filtersResponse.data) ? filtersResponse.data : []).map(normalizeFilter);
      setFilters(nextFilters);
      setSavedFilters(nextFilters);
      setCatalog(Array.isArray(catalogResponse.data) ? catalogResponse.data.map(String) : []);
    } catch (error) {
      setLoadError(getErrorMessage(error, "Failed to load product filters."));
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => onCountChange?.(filters.filter((filter) => filter.enabled).length), [filters, onCountChange]);

  const errors = useMemo(() => validateFilters(filters), [filters]);
  const hasErrors = errors.some((error) => Object.keys(error).length > 0);
  const dirty = JSON.stringify(filters) !== JSON.stringify(savedFilters);

  const updateFilter = (index, field, value) => {
    setFilters((current) => current.map((filter, filterIndex) => filterIndex === index ? { ...filter, [field]: value } : filter));
    setFeedback(null);
  };

  const saveFilters = async () => {
    if (hasErrors || !tenantId) return;
    setSaving(true);
    setFeedback(null);
    try {
      const payload = filters.map((filter) => ({ name: filter.name.trim(), field: filter.field, enabled: filter.enabled }));
      const { data } = await axiosInstance.put("/product-filters", payload, { headers: { tenantId } });
      const nextFilters = (Array.isArray(data) ? data : payload).map(normalizeFilter);
      setFilters(nextFilters);
      setSavedFilters(nextFilters);
      setFeedback({ severity: "success", message: "Product filters saved successfully." });
    } catch (error) {
      setFeedback({ severity: "error", message: getErrorMessage(error, "Failed to save product filters.") });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Stack alignItems="center" spacing={1.5} sx={{ py: 8 }}><CircularProgress size={30} sx={{ color: "#f97316" }} /><Typography sx={{ color: "#64748b" }}>Loading product filters…</Typography></Stack>;
  if (loadError) return <Alert severity="error" action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}>{loadError}</Alert>;

  return (
    <Stack spacing={2}>
      <Alert severity="info" sx={{ borderRadius: "14px" }}>Enabled filters are shown to shoppers in this order. Names are public; product field paths remain internal.</Alert>
      {feedback ? <Alert severity={feedback.severity} sx={{ borderRadius: "12px" }}>{feedback.message}</Alert> : null}
      <StatusBar dirty={dirty} saving={saving} noun="filters" onDiscard={() => { setFilters(savedFilters.map((filter) => ({ ...filter }))); setFeedback(null); }} onSave={saveFilters} saveDisabled={hasErrors} primaryButtonSx={primaryButtonSx} neutralButtonSx={neutralButtonSx} />
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }} justifyContent="space-between">
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography sx={{ fontWeight: 800, color: "#202531" }}>Filters</Typography>
          <Chip size="small" label={`${filters.filter((filter) => filter.enabled).length} active · ${filters.length} total`} sx={{ fontWeight: 700, backgroundColor: "rgba(249,115,22,0.12)", color: "#ea580c" }} />
        </Stack>
        <Button startIcon={<AddCircleOutlineIcon />} variant="outlined" size="small" onClick={() => setFilters((current) => [...current, { name: "", field: "", enabled: true }])} sx={neutralButtonSx}>Add filter</Button>
      </Stack>
      {filters.length === 0 ? (
        <Paper elevation={0} sx={{ p: 5, textAlign: "center", borderRadius: "16px", border: "1px dashed rgba(249,115,22,0.35)", backgroundColor: "rgba(255,247,241,0.45)" }}>
          <Typography sx={{ fontWeight: 800, color: "#202531" }}>No product filters yet</Typography>
          <Typography variant="body2" sx={{ mt: 0.5, mb: 2, color: "#64748b" }}>Add a filter to let shoppers refine product search results.</Typography>
          <Button startIcon={<AddCircleOutlineIcon />} variant="contained" onClick={() => setFilters([{ name: "", field: "", enabled: true }])} sx={primaryButtonSx}>Add first filter</Button>
        </Paper>
      ) : filters.map((filter, index) => (
        <Paper key={index} data-testid={`product-filter-${index}`} elevation={0} sx={{ p: 1.5, borderRadius: "16px", border: "1px solid rgba(15,23,42,0.08)", backgroundColor: "rgba(255,255,255,0.9)" }}>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "auto minmax(0,1fr) auto", md: "64px minmax(180px,0.8fr) minmax(260px,1.35fr) auto auto" }, gap: 1.25, alignItems: "center" }}>
            <Stack direction="row" spacing={0} sx={{ gridColumn: { xs: "1", md: "1" } }}>
              <Tooltip title="Move up"><span><IconButton size="small" aria-label={`Move filter ${index + 1} up`} disabled={index === 0} onClick={() => setFilters((current) => reorder(current, index, -1))}><ArrowUpwardIcon sx={{ fontSize: 17 }} /></IconButton></span></Tooltip>
              <Tooltip title="Move down"><span><IconButton size="small" aria-label={`Move filter ${index + 1} down`} disabled={index === filters.length - 1} onClick={() => setFilters((current) => reorder(current, index, 1))}><ArrowDownwardIcon sx={{ fontSize: 17 }} /></IconButton></span></Tooltip>
            </Stack>
            <TextField fullWidth size="small" label="Display name" value={filter.name} onChange={(event) => updateFilter(index, "name", event.target.value)} error={Boolean(errors[index]?.name)} helperText={errors[index]?.name || "Shown to shoppers"} inputProps={{ "aria-label": `Filter ${index + 1} display name` }} sx={{ ...inputSx, gridColumn: { xs: "2 / -1", md: "2" } }} />
            <Autocomplete
              options={catalog}
              value={filter.field || null}
              onChange={(_, value) => updateFilter(index, "field", value || "")}
              getOptionLabel={fieldLabel}
              renderInput={(params) => <TextField {...params} size="small" label="Product field" error={Boolean(errors[index]?.field)} helperText={errors[index]?.field || "Internal field mapping"} inputProps={{ ...params.inputProps, "aria-label": `Filter ${index + 1} product field` }} sx={inputSx} />}
              sx={{ gridColumn: { xs: "1 / -1", md: "3" } }}
            />
            <Chip label={fieldType(filter.field)} size="small" sx={{ justifySelf: "start", fontWeight: 700, color: "#475569", backgroundColor: "#f1f5f9" }} />
            <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
              <FormControlLabel control={<Switch size="small" checked={filter.enabled} onChange={(event) => updateFilter(index, "enabled", event.target.checked)} inputProps={{ "aria-label": `Filter ${index + 1} enabled` }} />} label={<Typography variant="caption" sx={{ fontWeight: 700 }}>{filter.enabled ? "Enabled" : "Disabled"}</Typography>} sx={{ m: 0 }} />
              <Tooltip title="Delete filter"><IconButton size="small" aria-label={`Delete filter ${index + 1}`} onClick={() => setFilters((current) => current.filter((_, filterIndex) => filterIndex !== index))} sx={{ color: "#dc2626" }}><DeleteOutlineIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
            </Stack>
          </Box>
        </Paper>
      ))}
    </Stack>
  );
};

const ProductDiscoverySection = ({ tenantDetails, setTenantDetails, onCountChange, primaryButtonSx = fallbackPrimaryButtonSx, neutralButtonSx = fallbackNeutralButtonSx }) => {
  const [tab, setTab] = useState("merchandising");
  const [counts, setCounts] = useState({ merchandising: 0, sorts: 0, filters: 0 });
  const setCount = useCallback((key, value) => setCounts((current) => current[key] === value ? current : { ...current, [key]: value }), []);
  const handleMerchandisingCount = useCallback((value) => setCount("merchandising", value), [setCount]);
  const handleSortCount = useCallback((value) => setCount("sorts", value), [setCount]);
  const handleFilterCount = useCallback((value) => setCount("filters", value), [setCount]);

  useEffect(() => onCountChange?.(counts.merchandising + counts.sorts + counts.filters), [counts, onCountChange]);

  return (
    <Stack spacing={2}>
      <Tabs value={tab} onChange={(_, nextTab) => setTab(nextTab)} variant="scrollable" scrollButtons="auto" aria-label="Product discovery configuration" sx={{ borderBottom: "1px solid rgba(15,23,42,0.1)", "& .MuiTab-root": { textTransform: "none", fontWeight: 750 }, "& .Mui-selected": { color: "#ea580c !important" }, "& .MuiTabs-indicator": { backgroundColor: "#f97316" } }}>
        <Tab value="merchandising" label={`Merchandising (${counts.merchandising})`} />
        <Tab value="sorts" label={`Sort options (${counts.sorts})`} />
        <Tab value="filters" label={`Filters (${counts.filters})`} />
      </Tabs>
      <Box role="tabpanel" hidden={tab !== "merchandising"} aria-label="Merchandising">
        <MerchandisingRulesSection tenantId={tenantDetails?.id} onRuleCountChange={handleMerchandisingCount} />
      </Box>
      <Box role="tabpanel" hidden={tab !== "sorts"} aria-label="Sort options">
        <SortOptionsEditor tenantDetails={tenantDetails} setTenantDetails={setTenantDetails} onCountChange={handleSortCount} primaryButtonSx={primaryButtonSx} neutralButtonSx={neutralButtonSx} />
      </Box>
      <Box role="tabpanel" hidden={tab !== "filters"} aria-label="Filters">
        <ProductFiltersEditor tenantId={tenantDetails?.id} onCountChange={handleFilterCount} primaryButtonSx={primaryButtonSx} neutralButtonSx={neutralButtonSx} />
      </Box>
    </Stack>
  );
};

export default ProductDiscoverySection;
